import { useCallback, useEffect, useRef, useState } from "react";
import type { ClinicianMedicalEvidence } from "@care-by-ella/contracts";
import {
  fetchMedicalEvidence,
  fetchPatientUploadBlob,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "@/components/SupportView";
import { customTaskBadge, isCompletionUpload } from "@/lib/custom-task-badge";
import { splitEvidenceUploads } from "@/lib/evidence-uploads";

// TES-590 — CP-06 as the doctor sees it: the tasks
// switched on after the consult with Klar/Saknas, and the evidence files in
// the SAME case, category by category. When everything the doctor asked for
// is done, the review is finished under Behandlingsbeslut (CP-09) — there is
// no CP-08 in between.

// TES-720 — an amount in "öre" → "995 kr". The price is product data, never copy.
function formatMinor(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amountMinor / 100);
}

const TASK_LABEL: Record<
  ClinicianMedicalEvidence["tasks"][number]["key"],
  string
> = {
  previous_lab_results: "Tidigare provsvar uppladdade",
  prescription_evidence: "Recept-/ordinationsunderlag uppladdat",
  // TES-720 — CP-06 Variant B: the payment is its own row and unlocks the
  // test row. Paid ≠ submitted ≠ received ≠ reviewed.
  lab_payment: "Blodprovet betalt",
  // TES-942 — no "svar mottagna": without a Webdoc integration nothing here
  // knows that a result has arrived. The row is about the sample.
  new_blood_test: "Nytt blodprov lämnat",
  blood_pressure_pulse: "Blodtryck och vilopuls registrerade",
};

const CATEGORY_LABEL: Record<string, string> = {
  PREVIOUS_LAB_RESULTS: "Tidigare provsvar",
  PRESCRIPTION_EVIDENCE: "Recept-/ordinationsunderlag",
};

// TES-942 — the sample row's badge, from the server's derived flags. Before
// payment it waits on the payment, not on her; her own report is shown as
// hers.
function sampleBadge(task: ClinicianMedicalEvidence["tasks"][number]): string {
  if (task.locked) return "Väntar på betalning";
  if (task.status !== "done") return "Saknas";
  return task.selfReported ? "Lämnat (patientens uppgift)" : "Klar";
}

export function MedicalEvidencePanel({
  patientId,
  onUnauthorized,
  reloadKey = 0,
  onLoaded,
}: {
  patientId: string;
  onUnauthorized: () => void;
  // TES-942 — bump to re-read after an action elsewhere on the page (a lab
  // order marked or reviewed), so the two panels never disagree.
  reloadKey?: number;
  // TES-942 — hands the response up, so the Blodprov tab reads the same
  // derived blood-test status without a second request.
  onLoaded?: (data: ClinicianMedicalEvidence) => void;
}) {
  // TES-1380 (Ö-598) — the admin's support view (TES-1040) sees that a
  // completion file exists, never the file.
  const supportView = useReadOnly();
  // Refs, not dependencies: the parent passes both callbacks inline, and
  // onLoaded sets the parent's state, so a callback in `load`'s dependencies
  // re-fetches on every parent render — an endless loop that QA's throttle
  // answered with 429 on the Blodprov tab.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const [data, setData] = useState<ClinicianMedicalEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openUrl, setOpenUrl] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchMedicalEvidence(patientId)
      .then((d) => {
        setData(d);
        onLoadedRef.current?.(d);
      })
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        else setError("Kunde inte hämta underlagen.");
      });
    // `reloadKey` is a trigger only: bumping it re-reads.
  }, [patientId, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (openUrl) URL.revokeObjectURL(openUrl);
    };
  }, [openUrl]);

  async function view(uploadId: string) {
    if (openId === uploadId) {
      setOpenId(null);
      if (openUrl) URL.revokeObjectURL(openUrl);
      setOpenUrl(null);
      return;
    }
    try {
      const blob = await fetchPatientUploadBlob(patientId, uploadId);
      if (openUrl) URL.revokeObjectURL(openUrl);
      setOpenUrl(URL.createObjectURL(blob));
      setOpenId(uploadId);
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError("Kunde inte öppna filen.");
    }
  }

  if (error) {
    return <p className="text-sm text-danger">{error}</p>;
  }
  if (!data) return null;

  // TES-738 — the same cutoff the checklist applies: files from THIS case
  // only. Without it a file from an earlier, closed consult sat beside a
  // task that read Saknas, and the doctor could not tell which was current.
  // TES-1029 — her F-07 files are listed apart and are never cut off:
  // she sent them for this transfer, before any consult.
  const { caseUploads, transferUploads } = splitEvidenceUploads(
    data.uploads,
    data.meetingDocumentedAt,
  );

  type EvidenceUpload = ClinicianMedicalEvidence["uploads"][number];
  const fileList = (uploads: EvidenceUpload[]) => (
    <ul className="flex flex-col gap-xs">
      {uploads.map((u) => (
        <li key={u.id} className="text-sm">
          {supportView && isCompletionUpload(u.fieldKey) ? (
            <span>Fil uppladdad (bara läkaren kan öppna den)</span>
          ) : (
            <button
              type="button"
              className="text-brand underline"
              onClick={() => void view(u.id)}
            >
              {u.filename}
            </button>
          )}
          <span className="text-textSecondary">
            {" "}
            —{" "}
            {CATEGORY_LABEL[u.category ?? ""] ??
              data.customTasks?.find((t) => t.fieldKey === u.fieldKey)
                ?.heading ??
              u.fieldKey}{" "}
            · {new Date(u.createdAt).toLocaleDateString("sv-SE")}
          </span>
          {openId === u.id && openUrl ? (
            <div className="mt-xs">
              {u.mimeType === "application/pdf" ? (
                // TES-738 — a PDF is not an <img>; embed it and offer
                // the download for viewers that block inline PDFs.
                <>
                  <iframe
                    src={openUrl}
                    title={u.filename}
                    className="w-full h-[560px] rounded-md border border-surfaceMuted"
                  />
                  <a
                    href={openUrl}
                    download={u.filename}
                    className="text-brand underline text-xs"
                  >
                    Ladda ner {u.filename}
                  </a>
                </>
              ) : (
                <img
                  src={openUrl}
                  alt={u.filename}
                  className="max-h-[480px] rounded-md border border-surfaceMuted"
                />
              )}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md mb-md">
      <h2 className="font-display text-lg mb-xs">Medicinskt underlag</h2>
      {!data.active ? (
        <p className="text-sm text-textSecondary">
          {data.meetingDocumentedAt
            ? "Inget underlag begärt, eller behandlingsbeslutet är publicerat."
            : "Inga uppgifter begärda än — de aktiveras när vårdmötet dokumenteras, eller när du begär ett nytt blodprov i ett receptövertag."}
        </p>
      ) : (
        <>
          <p className="text-sm text-textSecondary mb-sm">
            {data.allDone
              ? "Allt du begärt är klart. Slutför granskningen under Behandlingsbeslut."
              : "Patienten kompletterar. Kortet ligger kvar på hennes hemskärm tills allt är klart."}
          </p>
          <ul className="flex flex-col gap-xs">
            {data.tasks.map((task) => (
              <li
                key={task.key}
                className="flex items-center justify-between gap-sm text-sm"
              >
                <span>
                  {TASK_LABEL[task.key]}
                  {task.key === "lab_payment" && task.payment ? (
                    <span className="block text-xs text-textSecondary">
                      {formatMinor(
                        task.payment.amountMinor,
                        task.payment.currency,
                      )}
                      {task.payment.paidAt
                        ? ` · betalt ${new Date(task.payment.paidAt).toLocaleDateString("sv-SE")}`
                        : ""}
                    </span>
                  ) : null}
                </span>
                <span
                  className={`rounded-full px-sm py-[2px] text-xs font-semibold ${
                    task.status === "done"
                      ? "bg-brand-accent text-brand"
                      : "bg-surfaceMuted text-danger"
                  }`}
                >
                  {task.key === "lab_payment"
                    ? task.status === "done"
                      ? "Betalt"
                      : "Ej betalt"
                    : task.key === "new_blood_test"
                      ? sampleBadge(task)
                      : task.status === "done"
                        ? "Klar"
                        : "Saknas"}
                </span>
              </li>
            ))}
            {/* TES-1000 (Ö-246 (b)) — his own "Annan specificerad
                komplettering" rows, counted like the rest. */}
            {(data.customTasks ?? []).map((task) => (
              <li
                key={task.id}
                className="flex items-center justify-between gap-sm text-sm"
              >
                <span>
                  {task.heading}
                  <span className="block text-xs text-textSecondary">
                    Annan specificerad komplettering
                    {task.uploadAllowed
                      ? ` · ${task.uploads} ${task.uploads === 1 ? "fil" : "filer"}`
                      : ""}
                  </span>
                </span>
                <span
                  className={`rounded-full px-sm py-[2px] text-xs font-semibold ${
                    task.status === "done"
                      ? "bg-brand-accent text-brand"
                      : "bg-surfaceMuted text-danger"
                  }`}
                >
                  {customTaskBadge(task)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {transferUploads.length > 0 ? (
        <div className="mt-md">
          <h3 className="text-xs font-semibold text-textSecondary mb-xs">
            Från receptövertaget (F-07)
          </h3>
          {fileList(transferUploads)}
        </div>
      ) : null}
      {caseUploads.length > 0 ? (
        <div className="mt-md">
          <h3 className="text-xs font-semibold text-textSecondary mb-xs">
            Uppladdade filer
          </h3>
          {fileList(caseUploads)}
        </div>
      ) : null}
    </section>
  );
}
